import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";

interface Profile {
  id: string;
  username: string;
  displayName: string;
  email: string;
  phone: string;
  avatarUrl: string;
  joinedAt: string;
  role: "user" | "business" | "admin";
}

interface Coupon {
  id: string;
  code: string;
  title: string;
  description: string;
  discountPercent: number | null;
  discountAmount: number | null;
  expiresAt: string | null;
}

type ModalState =
  | { type: "none" }
  | { type: "email"; user: Profile }
  | { type: "coupon"; user: Profile }
  | { type: "createCoupon" }
  | { type: "role"; user: Profile };

const ROLE_LABELS: Record<string, string> = {
  user: "Üye",
  business: "İşletme",
  admin: "Yönetici",
};

const ROLE_COLORS: Record<string, string> = {
  user: "bg-stone-100 text-stone-600",
  business: "bg-amber-100 text-amber-700",
  admin: "bg-emerald-100 text-emerald-700",
};

const ROLE_ICONS: Record<string, string> = {
  user: "ri-user-line",
  business: "ri-store-2-line",
  admin: "ri-shield-star-line",
};

export default function UsersSection() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "user" | "business" | "admin">("all");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>({ type: "none" });

  // Email form state
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState("");

  // Coupon assign state
  const [selectedCouponId, setSelectedCouponId] = useState("");
  const [couponAssigning, setCouponAssigning] = useState(false);
  const [couponResult, setCouponResult] = useState("");

  // Create coupon form
  const [newCoupon, setNewCoupon] = useState({ code: "", title: "", description: "", discountPercent: "", expiresAt: "" });
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  // Role change state
  const [selectedRole, setSelectedRole] = useState<"user" | "business" | "admin">("user");
  const [roleChanging, setRoleChanging] = useState(false);
  const [roleResult, setRoleResult] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("profiles")
      .select("id, username, display_name, email, phone, avatar_url, joined_at, role")
      .order("joined_at", { ascending: false });
    if (data) {
      setUsers(data.map((r) => ({
        id: r.id as string,
        username: r.username as string,
        displayName: (r.display_name as string) || (r.username as string) || "—",
        email: r.email as string,
        phone: (r.phone as string) ?? "",
        avatarUrl: (r.avatar_url as string) ?? "",
        joinedAt: r.joined_at as string,
        role: ((r.role as string) === "business" || (r.role as string) === "admin")
          ? (r.role as "business" | "admin")
          : "user",
      })));
    }
    setLoading(false);
  }, []);

  const fetchCoupons = useCallback(async () => {
    const { data } = await supabase.from("coupons").select("*").order("created_at", { ascending: false });
    if (data) {
      setCoupons(data.map((r) => ({
        id: r.id as string,
        code: r.code as string,
        title: r.title as string,
        description: r.description as string,
        discountPercent: r.discount_percent as number | null,
        discountAmount: r.discount_amount as number | null,
        expiresAt: r.expires_at as string | null,
      })));
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchCoupons(); }, [fetchUsers, fetchCoupons]);

  const filtered = users.filter((u) => {
    const matchSearch =
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      u.displayName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const handleSendEmail = async () => {
    if (modal.type !== "email") return;
    setEmailSending(true);
    setEmailResult("");
    try {
      const res = await supabase.functions.invoke("admin-send-email", {
        body: {
          to: modal.user.email,
          toName: modal.user.username,
          subject: emailSubject,
          message: emailMessage,
        },
      });
      if (res.error) setEmailResult("Hata: " + res.error.message);
      else setEmailResult("✅ Mail başarıyla gönderildi!");
    } catch {
      setEmailResult("Bir hata oluştu.");
    }
    setEmailSending(false);
  };

  const handleAssignCoupon = async () => {
    if (modal.type !== "coupon" || !selectedCouponId) return;
    setCouponAssigning(true);
    setCouponResult("");
    const { error } = await supabase.from("user_coupons").insert({
      user_id: modal.user.id,
      coupon_id: selectedCouponId,
    });
    if (error) {
      if (error.code === "23505") setCouponResult("Bu kupon zaten bu kullanıcıya tanımlı.");
      else setCouponResult("Hata: " + error.message);
    } else {
      setCouponResult("✅ Kupon başarıyla tanımlandı!");
    }
    setCouponAssigning(false);
  };

  const handleCreateCoupon = async () => {
    if (!newCoupon.code || !newCoupon.title) return;
    setCreatingCoupon(true);
    const { error } = await supabase.from("coupons").insert({
      code: newCoupon.code.toUpperCase(),
      title: newCoupon.title,
      description: newCoupon.description,
      discount_percent: newCoupon.discountPercent ? parseInt(newCoupon.discountPercent) : null,
      expires_at: newCoupon.expiresAt || null,
    });
    if (!error) {
      await fetchCoupons();
      setNewCoupon({ code: "", title: "", description: "", discountPercent: "", expiresAt: "" });
      setModal({ type: "none" });
    }
    setCreatingCoupon(false);
  };

  const handleRoleChange = async () => {
    if (modal.type !== "role") return;
    setRoleChanging(true);
    setRoleResult("");
    const { error } = await supabase
      .from("profiles")
      .update({ role: selectedRole })
      .eq("id", modal.user.id);
    if (error) {
      setRoleResult("Hata: " + error.message);
    } else {
      setRoleResult(
        `✅ Rol başarıyla güncellendi! ${selectedRole === "admin" ? "Kullanıcı çıkış yapıp tekrar giriş yaptığında admin paneline erişebilir." : selectedRole === "business" ? "Kullanıcı çıkış yapıp tekrar giriş yaptığında işletme paneline erişebilir." : "Kullanıcının yetkileri kaldırıldı."}`
      );
      setUsers((prev) =>
        prev.map((u) => u.id === modal.user.id ? { ...u, role: selectedRole } : u),
      );
    }
    setRoleChanging(false);
  };

  const openRoleModal = (user: Profile) => {
    setSelectedRole(user.role);
    setRoleResult("");
    setModal({ type: "role", user });
  };

  const closeModal = () => {
    setModal({ type: "none" });
    setEmailSubject(""); setEmailMessage(""); setEmailResult("");
    setSelectedCouponId(""); setCouponResult("");
    setRoleResult("");
  };

  const inputCls = "w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors";

  const roleCounts = {
    all: users.length,
    user: users.filter((u) => u.role === "user").length,
    business: users.filter((u) => u.role === "business").length,
    admin: users.filter((u) => u.role === "admin").length,
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl p-4 border border-stone-100 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Kullanıcı adı veya e-posta ara..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-emerald-400" />
        </div>
        <span className="text-xs text-stone-400">{filtered.length} üye</span>
        <button onClick={() => setModal({ type: "createCoupon" })}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-coupon-line" /> Kupon Oluştur
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Toplam Üye", value: users.length, icon: "ri-user-line", color: "text-stone-700 bg-stone-100" },
          { label: "Normal Üye", value: roleCounts.user, icon: "ri-user-line", color: "text-stone-600 bg-stone-50 border border-stone-200" },
          { label: "İşletme", value: roleCounts.business, icon: "ri-store-2-line", color: "text-amber-700 bg-amber-50" },
          { label: "Yönetici", value: roleCounts.admin, icon: "ri-shield-star-line", color: "text-emerald-700 bg-emerald-50" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-stone-100 p-5 flex items-center gap-4">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${s.color}`}>
              <i className={`${s.icon} text-xl`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{s.value}</p>
              <p className="text-xs text-stone-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Role Filter Tabs */}
      <div className="bg-white rounded-2xl border border-stone-100 p-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-stone-500 mr-1">Rol Filtresi:</span>
        {(["all", "user", "business", "admin"] as const).map((r) => (
          <button
            key={r}
            onClick={() => setRoleFilter(r)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
              roleFilter === r
                ? "bg-emerald-700 text-white"
                : "bg-stone-100 text-stone-600 hover:bg-stone-200"
            }`}
          >
            {r !== "all" && <i className={`${ROLE_ICONS[r]} text-xs`} />}
            {r === "all" ? `Tümü (${roleCounts.all})` : `${ROLE_LABELS[r]} (${roleCounts[r]})`}
          </button>
        ))}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                {["Kullanıcı", "Rol", "Telefon", "Katılım", "İşlem"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {loading ? (
                <tr><td colSpan={5} className="py-12 text-center text-stone-400 text-sm">Yükleniyor...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="py-12 text-center text-stone-400 text-sm">Kullanıcı bulunamadı</td></tr>
              ) : filtered.map((u) => (
                <tr key={u.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.avatarUrl ? (
                        <img src={u.avatarUrl} alt={u.displayName} className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {u.displayName.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-stone-800">{u.displayName}</p>
                        <p className="text-xs text-stone-400">@{u.username} · {u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${ROLE_COLORS[u.role]}`}>
                      <i className={`${ROLE_ICONS[u.role]} text-xs`} />
                      {ROLE_LABELS[u.role]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-500">{u.phone ? `+90 ${u.phone}` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-stone-500">
                    {new Date(u.joinedAt).toLocaleDateString("tr-TR", { year: "numeric", month: "short", day: "numeric" })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openRoleModal(u)}
                        title="Rol Değiştir"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors"
                      >
                        <i className="ri-shield-user-line text-sm" />
                      </button>
                      <button onClick={() => { setModal({ type: "email", user: u }); }}
                        title="Mail Gönder"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors">
                        <i className="ri-mail-send-line text-sm" />
                      </button>
                      <button onClick={() => { setModal({ type: "coupon", user: u }); }}
                        title="Kupon Tanımla"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-colors">
                        <i className="ri-coupon-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Coupons List */}
      {coupons.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5">
          <h3 className="font-bold text-stone-900 mb-4">Mevcut Kuponlar</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {coupons.map((c) => (
              <div key={c.id} className="border border-dashed border-amber-300 rounded-xl p-4 bg-amber-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono font-bold text-amber-700 text-sm">{c.code}</span>
                  {c.discountPercent && (
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-semibold">%{c.discountPercent}</span>
                  )}
                </div>
                <p className="text-sm font-medium text-stone-800">{c.title}</p>
                {c.description && <p className="text-xs text-stone-500 mt-0.5">{c.description}</p>}
                {c.expiresAt && (
                  <p className="text-xs text-stone-400 mt-1">Son: {new Date(c.expiresAt).toLocaleDateString("tr-TR")}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === ROLE CHANGE MODAL === */}
      {modal.type === "role" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-stone-900 text-lg">Rol Değiştir</h3>
                <p className="text-sm text-stone-500">{modal.user.displayName} · {modal.user.email}</p>
              </div>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 cursor-pointer text-stone-400">
                <i className="ri-close-line" />
              </button>
            </div>

            {/* Current Role */}
            <div className="mb-5 p-3 bg-stone-50 rounded-xl flex items-center gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${ROLE_COLORS[modal.user.role]}`}>
                <i className={`${ROLE_ICONS[modal.user.role]} text-base`} />
              </div>
              <div>
                <p className="text-xs text-stone-500">Mevcut Rol</p>
                <p className="text-sm font-semibold text-stone-800">{ROLE_LABELS[modal.user.role]}</p>
              </div>
            </div>

            {/* Role Options */}
            <div className="space-y-2 mb-5">
              <p className="text-sm font-medium text-stone-700 mb-3">Yeni Rol Seç</p>
              {(["user", "business", "admin"] as const).map((role) => (
                <button
                  key={role}
                  onClick={() => setSelectedRole(role)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                    selectedRole === role
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-stone-100 hover:border-stone-200 bg-white"
                  }`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${ROLE_COLORS[role]}`}>
                    <i className={`${ROLE_ICONS[role]} text-base`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-stone-800">{ROLE_LABELS[role]}</p>
                    <p className="text-xs text-stone-500">
                      {role === "user" && "Standart kullanıcı — temel özellikler"}
                      {role === "business" && "İşletme paneline erişim — mekan yönetimi"}
                      {role === "admin" && "Tam yönetici yetkisi — tüm panellere erişim"}
                    </p>
                  </div>
                  {selectedRole === role && (
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-500 flex-shrink-0">
                      <i className="ri-check-line text-white text-xs" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Warning for admin */}
            {selectedRole === "admin" && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-600">
                  <strong>Dikkat:</strong> Yönetici rolü tüm admin paneline tam erişim sağlar. Bu işlemi yalnızca güvendiğiniz kişiler için yapın.
                </p>
              </div>
            )}

            {roleResult && (
              <p className={`text-sm px-4 py-3 rounded-xl mb-4 ${roleResult.startsWith("✅") ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                {roleResult}
              </p>
            )}

            <div className="flex gap-3">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 cursor-pointer">
                İptal
              </button>
              <button
                onClick={handleRoleChange}
                disabled={roleChanging || selectedRole === modal.user.role}
                className="flex-1 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer transition-colors"
              >
                {roleChanging ? "Güncelleniyor..." : selectedRole === modal.user.role ? "Değişiklik Yok" : "Rolü Güncelle"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === SEND EMAIL MODAL === */}
      {modal.type === "email" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-stone-900 text-lg">Mail Gönder</h3>
                <p className="text-sm text-stone-500">{modal.user.displayName} · {modal.user.email}</p>
              </div>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 cursor-pointer text-stone-400">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Konu</label>
                <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Mail konusu..." className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Mesaj</label>
                <textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)}
                  rows={6} placeholder="Mesajınızı yazın..."
                  className={`${inputCls} resize-none`} maxLength={2000} />
              </div>
              {emailResult && (
                <p className={`text-sm px-4 py-3 rounded-xl ${emailResult.startsWith("✅") ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                  {emailResult}
                </p>
              )}
              <div className="flex gap-3 pt-1">
                <button onClick={closeModal} className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 cursor-pointer">İptal</button>
                <button onClick={handleSendEmail} disabled={emailSending || !emailSubject || !emailMessage}
                  className="flex-1 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer">
                  {emailSending ? "Gönderiliyor..." : "Gönder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === ASSIGN COUPON MODAL === */}
      {modal.type === "coupon" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-stone-900 text-lg">Kupon Tanımla</h3>
                <p className="text-sm text-stone-500">{modal.user.displayName}</p>
              </div>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 cursor-pointer text-stone-400">
                <i className="ri-close-line" />
              </button>
            </div>
            {coupons.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-stone-500 text-sm mb-3">Henüz kupon oluşturulmamış.</p>
                <button onClick={() => setModal({ type: "createCoupon" })}
                  className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold cursor-pointer">
                  Kupon Oluştur
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Kupon Seç</label>
                  <select value={selectedCouponId} onChange={(e) => setSelectedCouponId(e.target.value)}
                    className={inputCls}>
                    <option value="">— Kupon seçin —</option>
                    {coupons.map((c) => (
                      <option key={c.id} value={c.id}>{c.code} — {c.title}{c.discountPercent ? ` (%${c.discountPercent})` : ""}</option>
                    ))}
                  </select>
                </div>
                {couponResult && (
                  <p className={`text-sm px-4 py-3 rounded-xl ${couponResult.startsWith("✅") ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                    {couponResult}
                  </p>
                )}
                <div className="flex gap-3">
                  <button onClick={closeModal} className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 cursor-pointer">İptal</button>
                  <button onClick={handleAssignCoupon} disabled={couponAssigning || !selectedCouponId}
                    className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer">
                    {couponAssigning ? "Tanımlanıyor..." : "Tanımla"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === CREATE COUPON MODAL === */}
      {modal.type === "createCoupon" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-stone-900 text-lg">Yeni Kupon Oluştur</h3>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 cursor-pointer text-stone-400">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Kupon Kodu <span className="text-red-400">*</span></label>
                <input value={newCoupon.code} onChange={(e) => setNewCoupon((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="BURSA2026" className={`${inputCls} font-mono uppercase`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Başlık <span className="text-red-400">*</span></label>
                <input value={newCoupon.title} onChange={(e) => setNewCoupon((p) => ({ ...p, title: e.target.value }))}
                  placeholder="%20 İndirim Kuponu" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Açıklama</label>
                <input value={newCoupon.description} onChange={(e) => setNewCoupon((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Tüm mekanlar için geçerli" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">İndirim Oranı (%)</label>
                  <input type="number" min="1" max="100" value={newCoupon.discountPercent}
                    onChange={(e) => setNewCoupon((p) => ({ ...p, discountPercent: e.target.value }))}
                    placeholder="20" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Son Kullanım</label>
                  <input type="date" value={newCoupon.expiresAt}
                    onChange={(e) => setNewCoupon((p) => ({ ...p, expiresAt: e.target.value }))}
                    className={inputCls} />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={closeModal} className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 cursor-pointer">İptal</button>
                <button onClick={handleCreateCoupon} disabled={creatingCoupon || !newCoupon.code || !newCoupon.title}
                  className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer">
                  {creatingCoupon ? "Oluşturuluyor..." : "Oluştur"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
